import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ShoppingCart, Phone, Menu, Truck, UserRound, LogOut, ShieldCheck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

const links = [
  { href: "#quote", label: "Instant Quote" },
  { href: "#services", label: "Services" },
  { href: "/used-goods", label: "Used Goods" },
  { href: "/merch", label: "Merch" },
  { href: "/reviews", label: "Reviews" },
  { href: "#how", label: "How It Works" },
];

const slug = (href) => href.replace(/[^a-z0-9]+/gi, "");

function NavLinkItem({ link, className, onClick, testId }) {
  const { pathname } = useLocation();
  const isHash = link.href.startsWith("#");
  const onHome = pathname === "/";
  if (isHash && onHome) {
    return <a href={link.href} onClick={onClick} data-testid={testId} className={className}>{link.label}</a>;
  }
  return <Link to={isHash ? `/${link.href}` : link.href} onClick={onClick} data-testid={testId} className={className}>{link.label}</Link>;
}

function AccountButton() {
  const { user, loading, login, logout } = useCustomerAuth();
  const navigate = useNavigate();
  if (loading) return null;
  if (!user) {
    return (
      <button data-testid="header-signin-btn" onClick={() => login("/account")} className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-zinc-200 text-sm font-bold text-zinc-800 hover:bg-zinc-50">
        <UserRound className="w-4 h-4 text-[#0C2E2B]" /> <span className="hidden sm:inline">Sign in</span>
      </button>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="header-account-menu-btn" className="inline-flex items-center gap-2 h-10 px-2 rounded-md border border-[#0C2E2B] hover:bg-zinc-50">
          {user.picture ? <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-7 h-7 rounded-full" /> : <span className="w-7 h-7 rounded-full bg-[#0C2E2B] text-[#FFDF00] grid place-items-center text-xs font-black">{(user.name || user.email)[0]?.toUpperCase()}</span>}
          <span className="hidden sm:inline text-sm font-bold text-zinc-800 max-w-[110px] truncate">{(user.name || user.email).split(" ")[0]}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem data-testid="header-account-link" onClick={() => navigate("/account")}><UserRound className="w-4 h-4 mr-2" /> My account</DropdownMenuItem>
        {user.role === "admin" && <DropdownMenuItem data-testid="header-admin-link" onClick={() => navigate("/admin")}><ShieldCheck className="w-4 h-4 mr-2" /> Admin console</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="header-signout-btn" onClick={logout}><LogOut className="w-4 h-4 mr-2" /> Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Header() {
  const { count, setOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const goToQuote = () => {
    if (pathname === "/") document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" });
    else navigate("/#quote");
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/85 border-b border-zinc-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-[#0C2E2B] grid place-items-center">
            <Truck className="w-5 h-5 text-[#FFDF00]" strokeWidth={2.5} />
          </div>
          <div className="leading-none">
            <div className="font-display font-black text-lg tracking-tight text-zinc-900">HARVEY'S PLACE</div>
            <div className="font-tactical text-[10px] uppercase tracking-[0.25em] text-[#0C2E2B]">Co. - Since '22</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLinkItem
              key={l.href}
              link={l}
              testId={`nav-${slug(l.href)}-link`}
              className="text-sm font-semibold text-zinc-700 hover:text-[#0C2E2B] transition-colors"
            />
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="tel:+16129907871"
            data-testid="header-call-btn"
            className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-200 text-sm font-bold text-zinc-800 hover:bg-zinc-50"
          >
            <Phone className="w-4 h-4 text-[#0C2E2B]" /> (612) 990-7871
          </a>
          <AccountButton />
          <button
            data-testid="header-cart-toggle-btn"
            onClick={() => setOpen(true)}
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-md border border-zinc-200 hover:bg-zinc-50"
            aria-label="Cart"
          >
            <ShoppingCart className="w-5 h-5 text-zinc-800" />
            {count > 0 && (
              <span
                data-testid="header-cart-count"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FFDF00] text-[#0C2E2B] text-[10px] font-bold grid place-items-center"
              >
                {count}
              </span>
            )}
          </button>
          <Button
            data-testid="header-instant-quote-btn"
            onClick={goToQuote}
            className="hidden md:inline-flex btn-kinetic rounded-md px-4 py-2 text-xs"
          >
            Get Instant Quote
          </Button>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-btn"
                className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-zinc-200"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetHeader>
                <SheetTitle className="font-display">Menu</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-4 mt-6">
                {links.map((l) => (
                  <NavLinkItem
                    key={l.href}
                    link={l}
                    onClick={() => setMobileOpen(false)}
                    testId={`mobile-nav-${slug(l.href)}-link`}
                    className="text-base font-semibold text-zinc-800 hover:text-[#0C2E2B]"
                  />
                ))}
                <Button
                  className="btn-kinetic rounded-md mt-4"
                  onClick={() => {
                    setMobileOpen(false);
                    goToQuote();
                  }}
                  data-testid="mobile-instant-quote-btn"
                >
                  Get Instant Quote
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
